import {
  ChatProvider,
  DEFAULT_CHAT_MODEL,
  DEFAULT_CHAT_PROVIDER,
  OPENROUTER_ONLY_CHAT_PROVIDERS,
  ProviderDefinition,
} from './structs/providers';

export const AI_EDITOR_PROVIDER_KEY = 'aiEditorProvider';
export const AI_EDITOR_MODEL_KEY = 'aiEditorModel';
export const AI_EDITOR_SETTINGS_EVENT = 'ai-editor-settings-change';
export const DEPRECATED_AI_EDITOR_MODEL = 'x-ai/grok-4-fast';

export const DEFAULT_AI_EDITOR_PROVIDER: ChatProvider = DEFAULT_CHAT_PROVIDER;
export const DEFAULT_AI_EDITOR_MODEL = DEFAULT_CHAT_MODEL;

const parseStoredString = (raw: string | null): string | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return raw;
  }
};

export const normalizeAiEditorModel = (value: unknown): string => {
  if (typeof value !== 'string') return DEFAULT_AI_EDITOR_MODEL;

  const trimmed = value.trim();
  if (!trimmed || trimmed === DEPRECATED_AI_EDITOR_MODEL) {
    return DEFAULT_AI_EDITOR_MODEL;
  }

  return trimmed;
};

/**
 * The AI Editor's backend handler (internal/api/clipeditor) hardcodes both of
 * its paths to OpenRouter — `ai.Generate(prompt, ai.ProviderOpenRouter, model)`
 * and `ai.StreamOpenRouterChat` — and carries no provider field at all, so its
 * settings pane must only offer OpenRouter. This is a distinct reason from the
 * shared constant's (that one is about the non-streaming Generate switch), so
 * the two lift independently. Restricting the list is the real fix;
 * `normalizeAiEditorProvider` below stays as the belt-and-braces guard against
 * a value already persisted in localStorage.
 */
export const AI_EDITOR_PROVIDERS: ProviderDefinition[] = OPENROUTER_ONLY_CHAT_PROVIDERS;

export const normalizeAiEditorProvider = (value: unknown): ChatProvider => {
  return value === 'openrouter' ? value : DEFAULT_AI_EDITOR_PROVIDER;
};

export const readStoredAiEditorProvider = (storage: Pick<Storage, 'getItem'>): ChatProvider => {
  return normalizeAiEditorProvider(parseStoredString(storage.getItem(AI_EDITOR_PROVIDER_KEY)));
};

export const readAndMigrateStoredAiEditorModel = (
  storage: Pick<Storage, 'getItem' | 'setItem'>
): string => {
  const raw = storage.getItem(AI_EDITOR_MODEL_KEY);
  const parsed = parseStoredString(raw);
  const normalized = normalizeAiEditorModel(parsed);

  if (raw && parsed !== normalized) {
    storage.setItem(AI_EDITOR_MODEL_KEY, JSON.stringify(normalized));
  }

  return normalized;
};
